import React, { useState, type FormEvent, type ReactNode } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Play,
  CheckCircle2,
  Trash2,
  Plus,
  Calendar,
} from 'lucide-react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Button } from '../../../shared/components/ui/Button'
import { Modal } from '../../../shared/components/ui/Modal'
import { BacklogIssueRow } from './BacklogIssueRow'
import {
  sortByBacklogOrder,
  sumStoryPoints,
  type EpicRef,
  type SubtaskStats,
} from '../boardUtils'
import type { Issue, Sprint, WorkflowStatus } from '../api'
import { cn, cardClassName } from '@jira-clone/ui'

function formatDateRange(start?: string | null, end?: string | null): string | null {
  if (!start && !end) return null
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  if (start && end) return `${fmt(start)} — ${fmt(end)}`
  return fmt((start ?? end) as string)
}

export interface SprintSectionProps {
  sprint: Sprint | null
  sectionId: string
  issues: Issue[]
  epicLookup: Map<string, EpicRef>
  subtaskStats: Map<string, SubtaskStats>
  assigneeNameById?: Map<string, string>
  statuses?: WorkflowStatus[]
  onUpdateStatus?: (issueId: string, statusId: string, category: string) => void
  onOpenIssue: (issueId: string) => void
  onCreateIssue?: (title: string, sprintId?: string | null) => void
  onStartSprint?: () => void
  onCompleteSprint?: () => void
  onDeleteSprint?: () => void
  starting?: boolean
  completing?: boolean
  selected?: Set<string>
  onToggleSelect?: (issueId: string, shiftKey: boolean) => void
  actions?: ReactNode
}

export function SprintSection({
  sprint,
  sectionId,
  issues,
  epicLookup,
  subtaskStats,
  assigneeNameById,
  statuses,
  onUpdateStatus,
  onOpenIssue,
  onCreateIssue,
  onStartSprint,
  onCompleteSprint,
  onDeleteSprint,
  starting = false,
  completing = false,
  selected,
  onToggleSelect,
  actions,
}: SprintSectionProps) {
  const [collapsed, setCollapsed] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [adding, setAdding] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')

  const { setNodeRef, isOver } = useDroppable({
    id: `section:${sectionId}`,
    data: { type: 'section', sectionId },
  })

  const title = sprint ? sprint.name : 'Backlog'
  const sortedIssues = sortByBacklogOrder(issues)
  const points = sumStoryPoints(issues)
  const dateRange = sprint ? formatDateRange(sprint.startDate ?? sprint.start_date, sprint.endDate ?? sprint.end_date) : null

  // Breakdown of story points: To Do, In Progress, Done
  const todoPts = issues
    .filter((i) => !i.status_category || i.status_category === 'todo')
    .reduce((sum, i) => sum + (i.story_points ?? i.estimatedHours ?? 0), 0)
  const inProgressPts = issues
    .filter((i) => i.status_category === 'in_progress')
    .reduce((sum, i) => sum + (i.story_points ?? i.estimatedHours ?? 0), 0)
  const donePts = issues
    .filter((i) => i.status_category === 'done')
    .reduce((sum, i) => sum + (i.story_points ?? i.estimatedHours ?? 0), 0)

  const handleQuickCreate = (e: FormEvent) => {
    e.preventDefault()
    if (!draftTitle.trim()) return
    onCreateIssue?.(draftTitle.trim(), sprint ? sprint.id : null)
    setDraftTitle('')
    setAdding(false)
  }

  return (
    <div className={cn(cardClassName(), 'mb-4 overflow-hidden')}>
      {/* Sprint Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-[#FAFBFC] dark:bg-slate-900/90 border-b border-gray-200/80 dark:border-slate-800 select-none">
        <div className="flex min-w-0 items-center gap-2.5">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:text-gray-900 dark:hover:text-slate-100 hover:bg-gray-200/60 dark:hover:bg-slate-800 transition-colors"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          <span className="text-sm font-bold text-[#172B4D] dark:text-slate-100 truncate">{title}</span>

          {sprint && (
            <span
              className={`rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ${
                sprint.status === 'active'
                  ? 'bg-[#E3FCEF] text-[#006644]'
                  : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300'
              }`}
            >
              {sprint.status === 'active' ? 'Active' : sprint.status === 'completed' ? 'Completed' : 'Future'}
            </span>
          )}

          <span className="text-xs font-normal text-gray-500 dark:text-slate-400">
            {issues.length} {issues.length === 1 ? 'issue' : 'issues'}
          </span>

          {dateRange && (
            <span className="hidden text-xs text-gray-400 dark:text-slate-500 sm:inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {dateRange}
            </span>
          )}

          {sprint?.goal && (
            <span className="hidden text-xs italic text-gray-500 dark:text-slate-400 lg:inline truncate max-w-xs">
              "{sprint.goal}"
            </span>
          )}
        </div>

        {/* Right side badges & actions */}
        <div className="flex items-center gap-3">
          {/* 3-color Estimation Badges */}
          {points > 0 && (
            <div className="flex items-center gap-1">
              {todoPts > 0 && (
                <span
                  title={`${todoPts} pts To Do`}
                  className="inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300 text-[10px] font-bold border border-gray-200 dark:border-slate-700"
                >
                  {todoPts}
                </span>
              )}
              {inProgressPts > 0 && (
                <span
                  title={`${inProgressPts} pts In Progress`}
                  className="inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full bg-[#DEEBFF] text-[#0747A6] text-[10px] font-bold"
                >
                  {inProgressPts}
                </span>
              )}
              {donePts > 0 && (
                <span
                  title={`${donePts} pts Done`}
                  className="inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full bg-[#E3FCEF] text-[#006644] text-[10px] font-bold"
                >
                  {donePts}
                </span>
              )}
            </div>
          )}

          {/* Sprint Lifecycle Buttons */}
          {sprint && sprint.status !== 'completed' && (
            <div className="flex items-center gap-2">
              {sprint.status !== 'active' && onStartSprint && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onStartSprint}
                  loading={starting}
                  className="h-7 text-xs gap-1.5 border-gray-300 dark:border-slate-700"
                >
                  <Play className="h-3.5 w-3.5 fill-current text-blue-600" />
                  <span>Start sprint</span>
                </Button>
              )}
              {sprint.status === 'active' && onCompleteSprint && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onCompleteSprint}
                  loading={completing}
                  className="h-7 text-xs gap-1.5 border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>Complete sprint</span>
                </Button>
              )}
              {onDeleteSprint && (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  title="Delete sprint"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {actions}
        </div>
      </div>

      {/* Issues Body */}
      {!collapsed && (
        <div
          ref={setNodeRef}
          className={`min-h-[48px] transition-colors ${
            isOver ? 'bg-blue-50/40 ring-1 ring-inset ring-blue-500/30 dark:bg-blue-950/20' : ''
          }`}
        >
          {issues.length === 0 && (
            <p className="px-4 py-6 text-center text-xs text-gray-400 dark:text-slate-500 italic">
              Plan a sprint by dragging issues here, or create a new issue below.
            </p>
          )}

          <SortableContext items={sortedIssues.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div>
              {sortedIssues.map((issue) => (
                <BacklogIssueRow
                  key={issue.id}
                  issue={issue}
                  sectionId={sectionId}
                  assigneeName={issue.assignee_id ? assigneeNameById?.get(issue.assignee_id) : undefined}
                  selected={selected?.has(issue.id)}
                  onToggleSelect={
                    onToggleSelect
                      ? (shiftKey) => onToggleSelect(issue.id, shiftKey)
                      : undefined
                  }
                  onClick={() => onOpenIssue(issue.id)}
                  epic={issue.parent_id ? epicLookup.get(issue.parent_id) : null}
                  subtaskStats={subtaskStats.get(issue.id)}
                  statuses={statuses}
                  onUpdateStatus={onUpdateStatus}
                />
              ))}
            </div>
          </SortableContext>

          {/* Inline "+ Create issue" button */}
          {onCreateIssue && (
            <div className="px-3 py-2 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
              {adding ? (
                <form onSubmit={handleQuickCreate} className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setAdding(false)
                    }}
                    onBlur={() => !draftTitle.trim() && setAdding(false)}
                    placeholder="What needs to be done? Press Enter to create"
                    className="flex-1 rounded border border-blue-500 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs text-gray-800 dark:text-slate-100 placeholder-gray-400 focus:outline-none ring-2 ring-blue-500/20"
                  />
                  <Button type="submit" size="sm" className="h-7 text-xs">
                    Create
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAdding(false)}
                    className="h-7 text-xs"
                  >
                    Cancel
                  </Button>
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors py-1 px-1 rounded"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Create issue</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Sprint Confirm Modal */}
      {sprint && confirmDelete && (
        <Modal
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          title={`Delete sprint "${sprint.name}"?`}
        >
          <div className="space-y-4">
            <p className="text-xs text-gray-600 dark:text-slate-300">
              Its issues will move back to the backlog. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setConfirmDelete(false)
                  onDeleteSprint?.()
                }}
              >
                Delete Sprint
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
